import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaReply, FaEye, FaCheck } from "react-icons/fa";
import BSPagination from "../../Components/pagination";
import LoadingSpinner from "../../Components/spinner";
import {
  fetchPaginatedFeedback,
  searchfeedbacks,
} from "../../services/axios/apiManager";
import { handleErrors } from "../../services/dataManager";
import { useNavigate } from "react-router-dom";
import FeedbackDetailsModal from "./feedbackDetailsModal";
import FeedbackRespondModal from "./respondModal";
import { toast } from "sonner";

import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";

const FeedbackList = ({
  feedbacks,
  setFeedbacks,
  pagination,
  setPagination,
  categories,
  granted,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState(2);
  const [filtering, setFiltering] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const tableRef = useRef(null);

  // Fetch feedbacks
  const fetchFeedbacks = async (pgn, filters = "") => {
    setFiltering(true);
    const res = await fetchPaginatedFeedback(
      pgn?.currentPage,
      pgn?.pageSize,
      filters
    );
    if (res?.data?.data) {
      setFeedbacks(res.data.data);
      res.data.pagination && setPagination(res.data.pagination);
    } else {
      handleErrors(res, navigate);
    }
    setFiltering(false);
  };

  // Pagination handler
  const handlePageChange = (page) => {
    const temp = { ...pagination };
    setPagination((prev) => ({ ...prev, currentPage: page }));
    temp.currentPage = page;
    fetchFeedbacks(temp);
  };

  // Reset filters
  const resetFilters = async () => {
    setStatus(2);
    setCategory("");
    setQuery("");
    await fetchFeedbacks(pagination);
  };

  // Category filter
  const fetchFbByCategory = async (e) => {
    const cat = e.target.value;
    setCategory(cat);
    setQuery("");
    if (cat) {
      const filters = `category=${cat}&status=${status}`;
      await fetchFeedbacks(pagination, filters);
    } else {
      await fetchFeedbacks(pagination);
    }
  };

  // Status filter
  const fetchFeedBackByStatus = async (e) => {
    const sts = e.target.value;
    setStatus(sts);
    setQuery("");
    const filters = `category=${category}&status=${sts}`;
    await fetchFeedbacks(pagination, filters);
  };

  // Search
  const handleSearch = async (e) => {
    const str = e.target.value;
    setQuery(str);
    setStatus(2);
    setCategory("");
    if (str) {
      setFiltering(true);
      const res = await searchfeedbacks(str);
      if (res?.data?.data) {
        setFeedbacks(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else {
        handleErrors(res, navigate, "Search feedbacks:");
      }
      setFiltering(false);
    } else {
      await fetchFeedbacks(pagination);
    }
  };

  // Modals
  const handleOpenDetailsModal = (fb) => {
    setSelectedData(fb);
    setShowDetailsModal(true);
  };
  const handleOpenRespondModal = (fb) => {
    if (!granted?.some((p) => p.type === "update")) {
      toast.error("You don't have neccessary permissions to do this operation.", { duration: 3000 });
      return;
    }
    setSelectedData(fb);
    setShowRespondModal(true);
  };

  // DataTable render
  useEffect(() => {
    const rows = feedbacks.map((fb) => {
      // clamp category if too long:
      const cat = `<div class="tb-preview">${fb.category}</div>`;
      const first = fb.first_name;
      const last = fb.last_name;
      const email = `<div class="tb-preview">${fb.email_address}</div>`;
      const resp = `
        <span class="${fb.responded ? "text-success" : "text-danger"}" data-id="${fb.id}" title=${fb.responded ? "Responded" : "Not-responded"}>
          ${fb.responded ? "Yes" : "No"}
        </span>`;
      const actions =
        `<button class="btn btn-sm btn-outline-success me-2 view-btn" data-id="${fb.id}" title="view details">
           <i class="fas fa-eye"></i>
         </button>` +
        (fb.responded
          ? `<button class="btn btn-sm btn-outline-info" disabled title="Already responded">
               <i class="fas fa-check"></i>
             </button>`
          : `<button class="btn btn-sm btn-outline-info respond-btn" data-id="${fb.id}" title="respond">
               <i class="fas fa-reply"></i>
             </button>`);

      return [cat, first, last, email, resp, actions];
    });

    const cfg = {
      data: rows,
      columns: [
        { title: "Category" },
        { title: "First Name" },
        { title: "Last Name" },
        { title: "Email Address" },
        { title: "Responded", className: "text-end", orderable: false },
        { title: "Action", className: "text-end", orderable: false },
      ],
      destroy: true,
      responsive: true,
      searching: false,
      paging: false,
      pageLength: pagination.pageSize || 10,
    };

    if ($.fn.dataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().clear().rows.add(rows).draw();
    } else {
      $(tableRef.current).DataTable(cfg);
    }

    const $tbl = $(tableRef.current);
    $tbl
      .off("click", ".view-btn")
      .on("click", ".view-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleOpenDetailsModal(feedbacks.find((f) => f.id === id));
      })
      .off("click", ".respond-btn")
      .on("click", ".respond-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleOpenRespondModal(feedbacks.find((f) => f.id === id));
      });

    return () => {
      if ($.fn.dataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().destroy();
      }
    };
  }, [feedbacks]);

  return (
    <div className="container py-4 text-light bg-dark rounded mb-4">
      {/* Filters */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-4 mb-2">
          <Form.Control
            placeholder="Search..."
            value={query}
            onChange={handleSearch}
            className="bg-dark text-light border-light"
          />
        </div>
        <div className="col-md-3 mb-2">
          <Form.Select
            value={category}
            onChange={fetchFbByCategory}
            className="bg-dark text-light border-light"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3 mb-2">
          <Form.Select
            value={status}
            onChange={fetchFeedBackByStatus}
            className="bg-dark text-light border-light"
          >
            <option value={2}>All Tickets</option>
            <option value={1}>Responded</option>
            <option value={0}>Not Responded</option>
          </Form.Select>
        </div>
        <div className="col-md-2 mb-2 text-end">
          {filtering ? (
            <LoadingSpinner size="md" />
          ) : (
            <Button variant="success" onClick={resetFilters} title="Reset filters and refresh records">
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* DataTable */}
      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-striped table-dark w-100"
        />
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-end mt-2">
        <div className="mx-4 py-2">
            <span className="text-light">{`Rows/Page ${pagination.pageSize || 0}, Total records ${pagination?.totalRecords || 0}`}</span>
          </div>
        <BSPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          siblingCount={2}
          showFirstLast
        />
      </div>

      {/* Modals */}
      <FeedbackDetailsModal
        showDetailsModal={showDetailsModal}
        setShowDetailsModal={setShowDetailsModal}
        selectedData={selectedData}
        setSelectedData={setSelectedData}
      />
      <FeedbackRespondModal
        showRespondModal={showRespondModal}
        setShowRespondModal={setShowRespondModal}
        selectedData={selectedData}
        setSelectedData={setSelectedData}
        pagination={pagination}
        fetchFeedbacks={fetchFeedbacks}
      />

      {/* Clamp CSS */}
      <style>{`
        .tb-preview {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default FeedbackList;
